/**
 * Time Machine #114 Phase 5 — pure helper that compares an event's before
 * and after images and returns a summary of what changed, suitable for
 * "Changed fields" display in <EventHistoryList>.
 *
 * Why this exists: the Atlas Trigger captures `updateFields` from MongoDB's
 * change stream for genuine `update` operations. But FileMaker-source writes
 * often arrive as `replace` events (full-document overwrites via Atlas Data
 * API) which DON'T populate updateFields. The user is left with the full
 * before/after JSON and no concise summary.
 *
 * This helper bridges the gap: if updateFields is missing, we compute a
 * before/after diff and use that. Skips metadata fields that are written by
 * every mutation regardless of business intent (timestamps, source attribution).
 *
 * Returns: an array of `{ key, before, after }` rows for fields that changed.
 * Each row's before/after is the raw value (caller renders).
 *
 * Pure / side-effect-free.
 */

// Fields that change on every write but don't represent business intent.
// Suppress them from the diff so users see only meaningful changes.
const NOISE_FIELDS = new Set([
  // Phase 1 source-attribution fields stamped by the Lambda mutations
  "_id",
  "_lastChangedAt",
  "_lastChangedBy",
  "_changeSource",
  // Job's history-log array (always grows on edit)
  "history",
  // Generic modification metadata seen in various docs
  "lastModifiedTimestamp",
  "lastUpdated",
  "updatedAt",
  "modifiedAt",
  // FileMaker auto-update fields (incremented on every write — added 2026-05-08
  // after dev observation that they appeared on every edit)
  "modificationTS",
  "modificationTS_invoiceRecord",
  "modCount",
]);

export function computeEventDiff(event) {
  if (!event || typeof event !== "object") return [];

  // Operation-specific cases first.
  if (event.operation === "create") {
    // For creates, "all fields" are new. Treat after's keys (minus noise) as
    // the change set; before is shown as undefined.
    if (!event.after || typeof event.after !== "object") return [];
    return Object.keys(event.after)
      .filter((k) => !NOISE_FIELDS.has(k))
      .sort()
      .map((key) => ({ key, before: undefined, after: event.after[key] }));
  }

  if (event.operation === "delete") {
    // For deletes, "all fields" are gone. Show before's keys.
    if (!event.before || typeof event.before !== "object") return [];
    return Object.keys(event.before)
      .filter((k) => !NOISE_FIELDS.has(k))
      .sort()
      .map((key) => ({ key, before: event.before[key], after: undefined }));
  }

  // For updates: prefer the trigger-supplied updateFields when present.
  // Mongo change streams fill this for genuine update operations. If not
  // present (e.g. replace events from FileMaker), compute from before/after.
  if (
    event.updateFields &&
    typeof event.updateFields === "object" &&
    Object.keys(event.updateFields).length > 0
  ) {
    return Object.keys(event.updateFields)
      .filter((k) => !NOISE_FIELDS.has(k))
      .sort()
      .map((key) => ({
        key,
        // before/after may be unavailable if the trigger only stored
        // updateFields; pull from before/after when present, else undefined.
        before:
          event.before && typeof event.before === "object"
            ? event.before[key]
            : undefined,
        after: event.updateFields[key],
      }));
  }

  // Fallback: client-side diff of before vs after.
  const before = event.before;
  const after = event.after;
  if (!before || !after || typeof before !== "object" || typeof after !== "object") {
    return [];
  }
  const allKeys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed = [];
  for (const key of allKeys) {
    if (NOISE_FIELDS.has(key)) continue;
    if (!shallowEqual(before[key], after[key])) {
      changed.push({ key, before: before[key], after: after[key] });
    }
  }
  changed.sort((a, b) => a.key.localeCompare(b.key));
  return changed;
}

/**
 * Shallow value equality. Handles primitives, dates, and one level of
 * arrays/objects. Deeper structures are JSON-compared as a safety net.
 * Good enough for "did this field change" — a true deep-equal isn't worth
 * pulling in lodash for.
 */
function shallowEqual(a, b) {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (typeof a !== typeof b) return false;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => shallowEqual(v, b[i]));
  }
  if (typeof a === "object" && typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return false;
}

export const __testables__ = { NOISE_FIELDS, shallowEqual };
